import { useCallback, useMemo, useState } from 'react';
import { Button, Modal, Notice, Spinner } from '../ui';
import { Link } from '../router/Link';
import type { ProblemCopy } from '../api/problem';
import { addTargetRows, type AddTargetRow } from './addModel';
import { CreatePlaylistForm } from './CreatePlaylistForm';
import { describeAuthProblem } from '../api/problem';
import { describeWriteProblem, ownedPlaylistsProblem, signInHereHref } from './model';
import { useCopy } from '../strings/useCopy';
import { useEntryMutations } from './mutations';
import { useOwnedPlaylists } from './useOwnedPlaylists';
import { useSession } from '../platform/session';
import styles from './addToPlaylist.module.css';

/**
 * §8's Watch row asks for **add-to-playlist**, and the whole design of this component is one measured
 * constraint: `GET /api/v1/auth/playlists` costs roughly 1.2 KB per entry across every playlist the
 * reader owns, with no summary form, no paging and `?fields=` ignored.
 *
 * So the list is fetched **when the dialog is opened**, never on mount. A Watch page that spent a
 * megabyte on arrival to populate a button nobody pressed would be the defect — and unlike 3b's avatar
 * problem there is no lazier granularity available: the endpoint has exactly one shape. That is also
 * what makes the button cheap enough to put on every card in a grid (2026-08-06): forty of them cost
 * nothing until one is pressed.
 *
 * A `Modal` rather than a `Menu`, and it used to be an in-flow disclosure. The reason it is not a
 * `Menu` still holds: that closes on any click inside it, which is right for choosing a subtitle track
 * and wrong here — adding two videos to two playlists is one visit, each row reports its own outcome,
 * and a popover that vanished on the first click would hide the "already in this playlist" answers the
 * (expensive) response paid for. The modal keeps all of that and stops the metadata column reflowing
 * under a list that can be long.
 *
 * Two faces since 2026-08-06, `WatchLaterButton`'s pair and for its reasons: `pill` on the watch
 * page's actions row, and `corner` — the glyph alone over a picture — on every grid card and in
 * /embed's top band, always directly below the Watch later clock. The corner face renders **nothing
 * signed out**, the same rule the clock beside it follows: a grid must not grow a per-card button
 * whose only act would be opening a sign-in, and inside an embed the sign-in link would navigate the
 * host page's iframe.
 */
export function AddToPlaylist({
  videoId,
  videoTitle,
  face = 'pill',
  cornerVariant = 'media',
  cornerSize = 'sm',
  tabIndex,
  signInHref,
  onDialogOpenChange,
}: {
  readonly videoId: string;
  /** For the corner face's accessible name — which video, like the clock above it says. */
  readonly videoTitle?: string;
  readonly face?: 'corner' | 'pill';
  /**
   * The corner face's Button variant: `media` over a thumbnail behind a scrim (history, the feed
   * grids), `ghost` where the row's own remove is a ghost on the page surface (playlist entries).
   */
  readonly cornerVariant?: 'media' | 'ghost';
  /** The corner face's Button size. `sm` in the grids, `md` in /embed's band — `WatchLaterButton`. */
  readonly cornerSize?: 'sm' | 'md';
  readonly tabIndex?: number;
  /**
   * `/login?next=<this video>` — built by the caller, so no href is assembled in a `.tsx` (R13). The
   * watch page passes its own because that one drops the `t`; anywhere else, `signInHereHref` comes
   * back to the page the reader is on, which is what a grid or a dialog wants anyway.
   */
  readonly signInHref?: string;
  /**
   * The dialog opened or closed. /embed is the caller that needs to know: its button rides in the
   * player's own top band, which fades and goes `inert` on the chrome's clock, and that clock can run
   * out while the dialog is up (`playlists/WatchLaterButton.tsx` has the measurement).
   */
  readonly onDialogOpenChange?: (open: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const session = useSession();
  const signedIn = session.ready && session.facts.signedIn;
  const owned = useOwnedPlaylists(open && signedIn);
  const entries = useEntryMutations();
  const [outcomes, setOutcomes] = useState<ReadonlyMap<string, 'added' | 'failed'>>(EMPTY);
  const [problem, setProblem] = useState<ProblemCopy | null>(null);
  const words = useCopy().text.playlists.add;

  const rows = useMemo(
    () => addTargetRows(owned.playlists, videoId, outcomes, entries.busy),
    [entries.busy, outcomes, owned.playlists, videoId],
  );

  const loadProblem = useMemo(
    () => (owned.problem === null ? null : describeAuthProblem(owned.problem, ownedPlaylistsProblem())),
    [owned.problem],
  );

  const add = useCallback(
    (playlistId: string) => {
      void (async () => {
        setProblem(null);
        const result = await entries.add(playlistId, videoId);
        setOutcomes((previous) => new Map(previous).set(playlistId, result.ok ? 'added' : 'failed'));
        if (!result.ok) setProblem(describeWriteProblem(result.problem, 'add'));
      })();
    },
    [entries, videoId],
  );

  const show = (next: boolean): void => {
    setOpen(next);
    onDialogOpenChange?.(next);
  };

  if (face === 'corner' && !signedIn) return null;

  /*
   * The corner face's hover word is the short act, and its accessible name says *which* video — the
   * split every corner control on a card makes: a screen reader has no picture to look at, while a
   * tooltip pops up over the card that is already showing the title.
   */
  const trigger =
    face === 'corner' ? (
      <Button
        size={cornerSize}
        variant={cornerVariant}
        icon
        tabIndex={tabIndex}
        label={words.addTo(videoTitle ?? videoId)}
        title={words.open}
        onClick={() => show(true)}
      >
        <PlaylistAddGlyph />
      </Button>
    ) : (
      /* The same pill the jump-out links beside it wear: one row, one shape of control. */
      <Button variant="pill" size="sm" onClick={() => show(true)}>
        <PlaylistAddGlyph />
        {words.open}
      </Button>
    );

  const dialog = (
    <Modal open={open} onClose={() => show(false)} title={words.open} closeLabel={words.close}>
      {!signedIn ? (
        <p className={styles.detail}>
          {words.signedOutBefore}{' '}
          <Link className={styles.link} href={signInHref ?? signInHereHref()}>
            {words.signIn}
          </Link>{' '}
          {words.signedOutAfter}
        </p>
      ) : (
        <div className={styles.content}>
          {owned.loading ? (
            // A `<div>`, not a `<p>`: `Spinner` renders a `<div>`, and React reports one inside a `<p>` as
            // a hydration error because the markup really is invalid — docs/specs/web-ui.md records the same
            // mistake made before. The spinner and the sentence sit side by side in a flex row instead.
            <div className={styles.detail} role="status">
              <Spinner />
              <span>{words.loading}</span>
            </div>
          ) : null}

          {loadProblem === null ? null : (
            // The classified copy, not `problem.kind`. The first pass rendered the raw discriminant, which
            // put "Could not load your playlists. transport" on screen — the taxonomy in `api/problem.ts`
            // exists exactly so that string never reaches a reader.
            <Notice tone="caution">
              <strong>{loadProblem.title}.</strong> {loadProblem.detail}
            </Notice>
          )}

          {/* One sentence, no jump-out: the create form is right below, and it saves this video. */}
          {rows !== null && rows.length === 0 ? (
            <p className={styles.detail}>{words.none}</p>
          ) : null}

          {rows === null || rows.length === 0 ? null : (
            <ul className={styles.list}>
              {rows.map((row) => (
                <Target key={row.playlistId} row={row} onAdd={add} />
              ))}
            </ul>
          )}

          {problem === null ? null : (
            <Notice tone="caution" live>
              <strong>{problem.title}.</strong> {problem.detail}
            </Notice>
          )}

          {/*
           * "Offer to create a new playlist for the video": creating from here also saves
           * the video — `onCreated` runs the same add path a row click does, so the fresh playlist
           * appears in the list above (the create invalidates it) already marked "Added".
           */}
          <CreatePlaylistForm
            id="add-to-playlist-create"
            submitLabel={words.createSubmit}
            onCreated={add}
          />
        </div>
      )}
    </Modal>
  );

  /*
   * The corner face adds no box of its own — it is a flex item in somebody else's row or column
   * (`listing/CardActions.tsx`, /embed's band), exactly like the Watch later clock beside it. The
   * pill face keeps its wrapper, which is what the actions row lays out.
   */
  return face === 'corner' ? (
    <>
      {trigger}
      {dialog}
    </>
  ) : (
    <div className={styles.wrap}>
      {trigger}
      {dialog}
    </div>
  );
}

function Target({
  row,
  onAdd,
}: {
  readonly row: AddTargetRow;
  readonly onAdd: (playlistId: string) => void;
}) {
  const words = useCopy().text.playlists.add;

  return (
    <li className={styles.row}>
      <button
        type="button"
        className={styles.rowButton}
        disabled={row.disabled}
        onClick={() => onAdd(row.playlistId)}
      >
        <span className={styles.rowTitle}>{row.title}</span>
        <span className={styles.rowMeta}>
          {row.countText === null ? null : <span>{row.countText}</span>}
          {/*
           * Three states, not two. "Cannot tell" is a real answer here — the list embeds only the first
           * 100 entries of each playlist, so a longer one may hold this video past the window — and
           * rendering it as "not in it" is how a reader ends up with a duplicate, which the endpoint
           * accepts without complaint (two adds of one videoId give two rows with different indexIds).
           */}
          {row.note === null ? null : <span className={styles.rowNote}>{row.note}</span>}
        </span>
        <span className={styles.rowState} aria-live="polite">
          {row.state === 'adding' ? <Spinner /> : null}
          {row.state === 'added' ? <span className={styles.added}>{words.added}</span> : null}
          {row.state === 'failed' ? <span className={styles.failed}>{words.failed}</span> : null}
        </span>
      </button>
    </li>
  );
}

const EMPTY: ReadonlyMap<string, 'added' | 'failed'> = new Map<string, 'added' | 'failed'>();

/**
 * Playlist lines gaining a plus — the Save to playlist verb, drawn where it is used the way the
 * Watch later button draws its clock: 18 px, the app's 24-grid stroke-1.8 hand.
 */
function PlaylistAddGlyph() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
      <path
        d="M4 6.5h12M4 11h12M4 15.5h7"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M17.5 13.5v5M15 16h5"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
